import { useState, useEffect, useRef } from "react";
import { Gift, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import storycraftLogo from "@/assets/storycraft-logo.png";

// Wheel segments - prizes have 0% chance, losing messages are weighted
const segments = [
  { label: "PS5 Console", color: "#f97316", isWin: true },
  { label: "Try harder!", color: "#fafafa", isWin: false },
  { label: "$15 Giftcard", color: "#f97316", isWin: true },
  { label: "Not today!", color: "#fafafa", isWin: false },
  { label: "10% Off", color: "#f97316", isWin: true },
  { label: "So close...", color: "#fafafa", isWin: false },
  { label: "Next time!", color: "#f97316", isWin: false },
  { label: "Won't spin!", color: "#fafafa", isWin: false },
  { label: "A for Effort!", color: "#f97316", isWin: false },
  { label: "Not this time", color: "#fafafa", isWin: false },
];

// Losing segment indices (all non-win segments)
const losingIndices = segments.map((s, i) => (!s.isWin ? i : -1)).filter(i => i !== -1);

const STORAGE_KEY = "spinWheel_lastSpin";

export default function SpinWheel() {
  const [isSpinning, setIsSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [canSpin, setCanSpin] = useState(true);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  const wheelRef = useRef<HTMLDivElement>(null);

  // Check if user can spin today
  useEffect(() => {
    const lastSpin = localStorage.getItem(STORAGE_KEY);
    if (lastSpin) {
      const lastSpinDate = new Date(lastSpin);
      const today = new Date();
      const isSameDay = 
        lastSpinDate.getFullYear() === today.getFullYear() &&
        lastSpinDate.getMonth() === today.getMonth() &&
        lastSpinDate.getDate() === today.getDate();
      
      setCanSpin(!isSameDay);
    }
  }, []);

  const spinWheel = () => {
    if (!canSpin || isSpinning) return;

    setIsSpinning(true);
    setResultMessage(null);

    // Always pick a losing segment
    const randomLosingIndex = losingIndices[Math.floor(Math.random() * losingIndices.length)];
    
    // Calculate the rotation to land on this segment
    const segmentAngle = 360 / segments.length;
    // Target angle for the segment (accounting for wheel orientation)
    const targetAngle = 360 - (randomLosingIndex * segmentAngle + segmentAngle / 2);
    
    // Add multiple full rotations (5-8) for drama
    const fullRotations = 5 + Math.floor(Math.random() * 3);
    const totalRotation = rotation + fullRotations * 360 + targetAngle;

    setRotation(totalRotation);

    // After spin completes
    setTimeout(() => {
      setIsSpinning(false);
      setCanSpin(false);
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
      
      const result = segments[randomLosingIndex];
      setResultMessage(result.label);
      toast.info(`${result.label} - Better luck tomorrow!`, {
        icon: "🎰",
      });
    }, 5000);
  };

  const segmentAngle = 360 / segments.length;

  return (
    <div className="p-6 md:p-8 min-h-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-gradient-to-br from-orange-500/20 to-amber-500/20">
            <Gift className="w-6 h-6 text-orange-400" />
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-foreground">
            The StoryCraft Wheel
          </h1>
        </div>
        <p className="text-muted-foreground">
          Spin daily for a chance to win amazing prizes!
        </p>
      </div>

      {/* Wheel Container */}
      <div className="flex flex-col items-center justify-center gap-8 max-w-2xl mx-auto">
        {/* Wheel */}
        <div className="relative">
          {/* Pointer/Arrow */}
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
            <div className="w-0 h-0 border-l-[15px] border-l-transparent border-r-[15px] border-r-transparent border-t-[25px] border-t-orange-500 drop-shadow-lg" />
          </div>
          
          {/* Outer glow ring */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-500/30 to-amber-500/30 blur-xl scale-110" />
          
          {/* Wheel border */}
          <div className="relative w-[320px] h-[320px] md:w-[400px] md:h-[400px] rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 p-2 shadow-2xl">
            {/* Wheel */}
            <div
              ref={wheelRef}
              className="w-full h-full rounded-full overflow-hidden relative"
              style={{
                transform: `rotate(${rotation}deg)`,
                transition: isSpinning 
                  ? "transform 5s cubic-bezier(0.17, 0.67, 0.12, 0.99)" 
                  : "none",
              }}
            >
              <svg viewBox="0 0 100 100" className="w-full h-full">
                {segments.map((segment, index) => {
                  const startAngle = index * segmentAngle;
                  const endAngle = startAngle + segmentAngle;
                  const startRad = (startAngle - 90) * (Math.PI / 180);
                  const endRad = (endAngle - 90) * (Math.PI / 180);
                  
                  const x1 = 50 + 50 * Math.cos(startRad);
                  const y1 = 50 + 50 * Math.sin(startRad);
                  const x2 = 50 + 50 * Math.cos(endRad);
                  const y2 = 50 + 50 * Math.sin(endRad);
                  
                  const largeArc = segmentAngle > 180 ? 1 : 0;
                  
                  // Text positioning
                  const midAngle = (startAngle + segmentAngle / 2 - 90) * (Math.PI / 180);
                  const textX = 50 + 35 * Math.cos(midAngle);
                  const textY = 50 + 35 * Math.sin(midAngle);
                  const textRotation = startAngle + segmentAngle / 2;

                  return (
                    <g key={index}>
                      <path
                        d={`M 50 50 L ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2} Z`}
                        fill={segment.color}
                        stroke="#27272a"
                        strokeWidth="0.3"
                      />
                      <text
                        x={textX}
                        y={textY}
                        fill={segment.color === "#fafafa" ? "#18181b" : "#fafafa"}
                        fontSize="3"
                        fontWeight="600"
                        textAnchor="middle"
                        dominantBaseline="middle"
                        transform={`rotate(${textRotation}, ${textX}, ${textY})`}
                      >
                        {segment.label}
                      </text>
                    </g>
                  );
                })}
              </svg>
            </div>
            
            {/* Center circle with logo */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 md:w-20 md:h-20 rounded-full bg-zinc-900 border-4 border-zinc-700 flex items-center justify-center shadow-lg z-10">
              <img src={storycraftLogo} alt="StoryCraft" className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-contain" />
            </div>
          </div>
        </div>

        {/* Spin Button */}
        <div className="flex flex-col items-center gap-3">
          <Button
            onClick={spinWheel}
            disabled={!canSpin || isSpinning}
            size="lg"
            className="px-10 py-6 text-lg bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold shadow-lg shadow-orange-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSpinning ? (
              <>
                <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                Spinning...
              </>
            ) : (
              "Spin and Win!"
            )}
          </Button>
          
          <p className="text-sm text-muted-foreground">
            {canSpin 
              ? "You have 1 spin available today" 
              : "Come back tomorrow for another spin!"
            }
          </p>
        </div>

        {/* Result Message */}
        {resultMessage && !isSpinning && (
          <div className="glass-card p-4 text-center animate-fade-in">
            <p className="text-lg font-semibold text-foreground">{resultMessage}</p>
            <p className="text-sm text-muted-foreground mt-1">Try again tomorrow!</p>
          </div>
        )}
      </div>
    </div>
  );
}
